var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var FillConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { DateConverter, JSON, JsonConverter, JsonDiscriminatorValue, JsonSchema } from '@symon-ai/wisepipe-serialize';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult } from './validation/toolValidationResult';
const schema = {
    $id: 'urn:wisepipe-common-pipe:FillConfig',
    type: 'object',
    properties: {
        selectedColumns: {
            type: 'array',
            items: { type: 'string', minLength: 1 },
            uniqueItems: true
        },
        stringFill: { type: 'string', minLength: 1 },
        numberFill: {
            type: ['number', 'string'],
            if: { type: 'string' },
            then: {
                // numberFill for decimal cols need numbers to be in string format to preserve precision
                oneOf: [{ enum: ['avg', 'median', 'mode'] }, { pattern: '^[-+]?[0-9]*\\.?[0-9]+([eE][-+]?[0-9]+)?$' }]
            }
        },
        // date format: http://json-schema.org/latest/json-schema-validation.html#rfc.section.7.3.1
        dateFill: { type: 'string', format: 'date-time' },
        booleanFill: { type: 'boolean' }
    },
    required: ['selectedColumns'],
    // We require selectedColumns, toolType (from the base class) and at least one other property. This isn't ideal because
    // it depends on counting the number of inherited properties but it should work for this usage. I couldn't find a simple
    // way to say "at least one of these properties". The alternative I could think of is enumerating all combinations of them
    // (stringFill, stringFill+numberFill), ...) which was worse. I may be missing something.
    minProperties: 3
};
let FillConfig = FillConfig_1 = class FillConfig extends ToolConfig {
    constructor(selectedColumns, fill, customFormats) {
        super(ToolType.FILL, customFormats);
        this.selectedColumns = selectedColumns;
        if (fill.string !== undefined) {
            this.stringFill = fill.string;
        }
        if (fill.number !== undefined) {
            this.numberFill = fill.number;
        }
        if (fill.date !== undefined) {
            this.dateFill = fill.date;
        }
        if (fill.boolean !== undefined) {
            this.booleanFill = fill.boolean;
        }
        JSON.validate(this, FillConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return FillConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return FillConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return FillConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return FillConfig_1.outs;
    }
    static get in() {
        return FillConfig_1.ins[0];
    }
    static get out() {
        return FillConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.FILL.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.CLEAN], this.ins, this.outs, ['impute'], true);
    }
    // eslint-disable-next-line class-methods-use-this
    get hasModel() {
        return true;
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const columns = ins.schemas.at(this.in);
        const exp = tsb.export(this.out);
        const fills = new Set(this.selectedColumns);
        if (columns) {
            columns.forEach((c) => {
                const isFillCol = fills.has(c.name);
                exp.copy(this.in, c, isFillCol, isFillCol);
            });
        }
        exp.initializeColumnFormats().setColumnFormats(this.customFormats);
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        // get columns
        const types = new Set();
        for (const selected of this.selectedColumns) {
            const column = source.find((c) => c.name === selected);
            if (column === undefined) {
                return ToolValidationResult.getBadMetadataResult(`Column ${selected} not found in source.`, `Please select valid columns from the source. Available columns: ${source.map((c) => c.name).join(', ')}.`);
            }
            types.add(column.type);
        }
        // check that we dont have any period type columns selected
        if (types.has('period')) {
            return ToolValidationResult.getInvalidResult('Period columns are not supported');
        }
        // check that we have fill values for all the types the user selected
        if (types.has('boolean') && this.booleanFill === undefined) {
            return ToolValidationResult.getBadMetadataResult('Boolean fill value is missing');
        }
        if (types.has('date') && this.dateFill === undefined) {
            return ToolValidationResult.getBadMetadataResult('Date fill value is missing');
        }
        if (types.has('number') && this.numberFill === undefined) {
            return ToolValidationResult.getBadMetadataResult('Number fill value is missing');
        }
        if (types.has('string') && this.stringFill === undefined) {
            return ToolValidationResult.getBadMetadataResult('String fill value is missing');
        }
        return ToolValidationResult.getValidResult();
    }
};
__decorate([
    JsonConverter(new DateConverter('date-time'))
], FillConfig.prototype, "dateFill", void 0);
FillConfig = FillConfig_1 = __decorate([
    JsonSchema(schema),
    JsonDiscriminatorValue(ToolType.FILL)
], FillConfig);
export { FillConfig };
//# sourceMappingURL=fillConfig.js.map